import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import usePageTitle from '../../hooks/usePageTitle';
import DashboardShell from "../../components/Dashboard/DashboardShell";
import UserCard from "../../components/Dashboard/Notifications/UserCard";
import { getMyFepks, getMyRequests, approveRequest, refuseRequest } from "../../api/epks";
import NotificationEpkCard from "../../components/Dashboard/Notifications/NotificationEpkCard";
import NewEpkBtn from "../../components/Dashboard/Epks/NewEpkBtn";
import RequestCard from "../../components/Dashboard/Notifications/RequestCard";
import EpkViewCard from "../../components/Dashboard/EpkViewCard";
import LoadingSpin from "../../components/Dashboard/LoadingSpin";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NotificationTextInfo from "../../components/Dashboard/Notifications/NotificationTextInfo";
import Sidebar from "../../components/Dashboard/Sidebar";
import RequestCardMobile from "../../components/Dashboard/Notifications/RequestCardMobile";

export default function NotificationPage() {
  usePageTitle('Notifications');
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [openTab, setOpenTab] = useState(1);
  const [selectedEpk, setSelectedEpk] = useState(0);
  const [selectedMobileEpkId, setSelectedMobileEpkId] = useState(null);
  const [likesSubTab, setLikesSubTab] = useState("likes");
  const [filter, setFilter] = useState("allRequests");
  const [epkList, setEpkList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [epkLoadFailed, setEpkLoadFailed] = useState(false);
  const [count, setCount] = useState(0);
  const [myRequests, setMyRequests] = useState({ approved: [], pending: [], refused: [] });
  const [loadingMyRequests, setLoadingMyRequests] = useState(true);

  // Derived
  const selectedDesktopEpk = epkList[selectedEpk] || {};
  const selectedMobileEpk  = epkList.find(e => e._id === selectedMobileEpkId) || epkList[0] || {};
  const hasEpks = epkList.length > 0;
  const showLeftPanel = openTab !== 3 && hasEpks;

  const pendingCount = epkList.reduce(
    (acc, epk) => acc + (epk.requests?.filter(r => r.status === "pending").length || 0), 0
  );
  const myPendingCount = myRequests.pending.length;

  const getUsers = (epk) => {
    if (!epk) return [];
    if (likesSubTab === "likes")   return epk.likes         || [];
    if (likesSubTab === "buy")     return epk.wishes_to_buy || [];
    if (likesSubTab === "follows") return epk.favourites    || [];
    return [];
  };

  // Data fetching
  useEffect(() => {
    setLoading(true);
    setLoadingMyRequests(true);
    getMyFepks()
      .then((res) => {
        setEpkList(res);
        setEpkLoadFailed(false);
        if (res.length > 0 && !selectedMobileEpkId) setSelectedMobileEpkId(res[0]._id);
      })
      // Keep whatever was already loaded: an empty list would read as "you have
      // no EPKs" and zero the pending-request badge.
      .catch(() => setEpkLoadFailed(true))
      .finally(() => setLoading(false));
    getMyRequests(user.id)
      .then((list) => {
        const byStatus = (status) => list.filter((item) =>
          item.requests?.some((req) => req.status === status)
        );
        setMyRequests({ approved: byStatus("approved"), pending: byStatus("pending"), refused: byStatus("refused") });
        setLoadingMyRequests(false);
      })
      .catch(() => setLoadingMyRequests(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, count]);

  // Handlers
  const handleSelectDesktopEpk = (index) => {
    setSelectedEpk(index);
    setLikesSubTab("likes");
  };

  const handleSelectMobileEpk = (epk) => {
    setSelectedMobileEpkId(epk._id);
    setLikesSubTab("likes");
    setFilter("allRequests");
  };

  const updateRequestStatus = (fepkId, requestUser, newStatus) => {
    setEpkList(prev => prev.map(epk =>
      epk._id === fepkId
        ? { ...epk, requests: epk.requests.map(r =>
            r.user === requestUser ? { ...r, status: newStatus } : r
          )}
        : epk
    ));
  };

  // Optimistic: flip the card now, put it back if the server says no. The
  // refetch then resyncs, but a network outage fails that too, so the local
  // revert is what keeps the card honest.
  const decide = (send, newStatus) => (request, fepkId) => {
    updateRequestStatus(fepkId, request.user, newStatus);
    send({ fepkId, user: request.user })
      .catch(() => {
        updateRequestStatus(fepkId, request.user, request.status);
        toast.error(t("Couldn't update the request. Please try again."));
        setCount(c => c + 1);
      });
  };
  const handleApprove = decide(approveRequest, "approved");
  const handleDeny = decide(refuseRequest, "refused");

  const handleMessageClick = (userId) => navigate(`/dashboard/chat/${userId}`);

  // Style helpers
  const mainTabClass = (active) =>
    `tw-flex-1 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-lg tw-cursor-pointer tw-transition-colors tw-border-0 tw-flex tw-items-center tw-justify-center tw-gap-1.5 ${
      active ? "tw-bg-[#712CB0] tw-text-white" : "tw-bg-transparent tw-text-white/50 hover:tw-text-white"
    }`;

  const subTabClass = (active) =>
    `tw-flex-1 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-lg tw-cursor-pointer tw-transition-colors tw-border-0 tw-flex tw-items-center tw-justify-center tw-gap-1 ${
      active ? "tw-bg-[#712CB0] tw-text-white" : "tw-bg-transparent tw-text-white/50 hover:tw-text-white"
    }`;

  const filterTabClass = (active) =>
    `tw-flex-1 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-full tw-cursor-pointer tw-transition-colors tw-border-0 ${
      active ? "tw-bg-[#712CB0] tw-text-white" : "tw-bg-transparent tw-text-white/50 hover:tw-text-white"
    }`;

  const retryEpkLoad = () => setCount((c) => c + 1);

  // Sub-components
  const EpkLoadError = () => (
    <div role="alert" className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-red-400/30 tw-bg-red-500/10 tw-px-4 tw-py-3 tw-text-center">
      <p className="tw-m-0 tw-text-sm tw-text-red-200">{t("We couldn't load your EPK notifications.")}</p>
      <button
        type="button"
        onClick={retryEpkLoad}
        className="tw-rounded-lg tw-border-0 tw-bg-[#712CB0] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer hover:tw-bg-[#8a3fd0]"
      >
        {t("Try again")}
      </button>
    </div>
  );

  // No EPKs to show: either the load failed or the filmmaker has none yet.
  const NoEpksState = ({ large }) =>
    epkLoadFailed ? (
      <div className={large ? "tw-pt-8" : "tw-pt-16"}>
        <EpkLoadError />
      </div>
    ) : (
      <div className={`tw-flex tw-flex-col tw-items-center ${large ? "tw-gap-8 tw-pt-8" : "tw-gap-4 tw-pt-16"}`}>
        <NewEpkBtn />
        <p className={large ? "tw-text-xl tw-font-light tw-text-white/40" : "tw-text-sm tw-text-white/40 tw-m-0"}>
          {t("You don't have any notifications yet.")}
        </p>
      </div>
    );

  const SubTabBar = ({ epk }) => (
    <div className="tw-flex tw-rounded-xl tw-bg-white/10 tw-p-1 tw-mb-4">
      {[
        { key: "likes",   label: t("Likes"),    count: epk?.likes?.length         ?? 0 },
        { key: "buy",     label: t("Would Buy"), count: epk?.wishes_to_buy?.length ?? 0 },
        { key: "follows", label: t("Follows"),   count: epk?.favourites?.length    ?? 0 },
      ].map(({ key, label, count }) => (
        <button key={key} onClick={() => setLikesSubTab(key)} className={subTabClass(likesSubTab === key)}>
          {label}
          <span className={`tw-rounded-full tw-px-1.5 tw-text-[10px] tw-font-bold ${likesSubTab === key ? "tw-bg-white/20" : "tw-bg-white/10 tw-text-white/40"}`}>
            {count}
          </span>
        </button>
      ))}
    </div>
  );

  const UserList = ({ epk }) => {
    const users = getUsers(epk);
    return users.length > 0 ? (
      <div className="tw-flex tw-flex-col tw-gap-2">
        {users.map((u) => (
          <UserCard
            key={u._id}
            UserInfo={{ role: u.role, firstName: u.firstName, lastName: u.lastName, website: u.website, id: u._id, picture: u.picture }}
            onMessageIconClick={() => handleMessageClick(u._id)}
          />
        ))}
      </div>
    ) : (
      <p className="tw-text-center tw-text-sm tw-text-white/30 tw-py-8 tw-m-0">
        {t("No activity yet for this EPK.")}
      </p>
    );
  };

  const RequestActions = ({ request, fepkId }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
      {request.status === "refused" && (
        <span className="tw-rounded-full tw-bg-red-900/30 tw-px-3 tw-py-1 tw-text-xs tw-text-red-300">{t("Refused")}</span>
      )}
      {request.status === "approved" && (
        <span className="tw-rounded-full tw-bg-green-900/30 tw-px-3 tw-py-1 tw-text-xs tw-text-green-300">{t("Approved")}</span>
      )}
      {request.status === "pending" && (
        <>
          <button onClick={() => handleApprove(request, fepkId)} className="tw-rounded-full tw-bg-[#712CB0] tw-px-4 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-[#8B3FD9] tw-transition-colors tw-border-0 tw-cursor-pointer">
            {t("Approve")}
          </button>
          <button onClick={() => handleDeny(request, fepkId)} className="tw-rounded-full tw-bg-white/10 tw-px-4 tw-py-1 tw-text-xs tw-text-white/70 hover:tw-bg-white/20 tw-transition-colors tw-border-0 tw-cursor-pointer">
            {t("Deny")}
          </button>
        </>
      )}
    </div>
  );

  const MyApplicationsPanel = () => {
    if (loadingMyRequests) return <LoadingSpin />;
    const total = myRequests.approved.length + myRequests.pending.length + myRequests.refused.length;
    if (total === 0) return (
      <p className="tw-text-center tw-text-sm tw-text-white/30 tw-py-8 tw-m-0">
        {t("You haven't applied to any EPKs yet.")}
      </p>
    );
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        {[
          { key: "pending",  label: t("Pending"),  colorClass: "tw-text-amber-300" },
          { key: "approved", label: t("Approved"), colorClass: "tw-text-green-300" },
          { key: "refused",  label: t("Refused"),  colorClass: "tw-text-red-300"   },
        ].map(({ key, label, colorClass }) =>
          myRequests[key].length > 0 && (
            <div key={key}>
              <p className={`tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-mb-3 tw-m-0 ${colorClass}`}>
                {label} ({myRequests[key].length})
              </p>
              <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-3">
                {myRequests[key].map((epk) => (
                  <EpkViewCard key={epk._id} EpkInfo={epk} />
                ))}
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  const EpkStrip = () => (
    <div className="tw-flex-shrink-0 tw-border-b tw-border-white/10 tw-py-3 tw-relative">
      {epkList.length > 3 && (
        <div className="tw-pointer-events-none tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-10 tw-z-10"
          style={{ background: "linear-gradient(to right, transparent, #1E0039)" }} />
      )}
      <div className="tw-flex tw-gap-2 tw-px-4 tw-overflow-x-auto [&::-webkit-scrollbar]:tw-hidden">
        {epkList.map((epk) => {
          const isSelected = selectedMobileEpk?._id === epk._id;
          const bannerUrl = epk.banner_url ? `${process.env.REACT_APP_AWS_URL}/${epk.banner_url}` : null;
          return (
            <button
              key={epk._id}
              onClick={() => handleSelectMobileEpk(epk)}
              className={`tw-flex-shrink-0 tw-flex tw-flex-col tw-rounded-xl tw-border tw-overflow-hidden tw-transition-all tw-cursor-pointer tw-bg-transparent tw-p-0 tw-w-28 ${
                isSelected ? "tw-border-[#712CB0]" : "tw-border-white/10"
              }`}
            >
              <div className="tw-w-28 tw-h-16 tw-bg-white/10">
                {bannerUrl
                  ? <img src={bannerUrl} alt={epk.title} className="tw-w-full tw-h-full tw-object-cover" />
                  : <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-white/20 tw-text-[10px]">{t("No image")}</div>
                }
              </div>
              <p className={`tw-m-0 tw-px-2 tw-py-1.5 tw-text-xs tw-truncate tw-w-28 tw-text-left ${isSelected ? "tw-text-white tw-font-medium" : "tw-text-white/40"}`}>
                {epk.title || t("Untitled")}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Render
  return (
    <>
      {/* Mobile */}
      <div className="tw-flex md:tw-hidden tw-flex-col tw-bg-[#1E0039] tw-h-screen tw-overflow-hidden">

        <div className="tw-flex-shrink-0 tw-pt-32 tw-px-4 tw-pb-3 tw-border-b tw-border-white/10 tw-bg-[#1E0039]">
          <h1 className="tw-text-lg tw-font-semibold tw-text-white tw-m-0 tw-mb-3">{t("Notifications")}</h1>
          <div className="tw-flex tw-rounded-xl tw-bg-white/10 tw-p-1">
            <button onClick={() => setOpenTab(1)} className={mainTabClass(openTab === 1)}>
              {t("Activity")}
            </button>
            <button onClick={() => setOpenTab(2)} className={mainTabClass(openTab === 2)}>
              {t("Requests")}
              {pendingCount > 0 && (
                <span className="tw-rounded-full tw-bg-red-500 tw-px-1.5 tw-text-[10px] tw-text-white tw-font-bold">
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </button>
            <button onClick={() => setOpenTab(3)} className={mainTabClass(openTab === 3)}>
              {t("Applied")}
              {myPendingCount > 0 && (
                <span className="tw-rounded-full tw-bg-amber-500 tw-px-1.5 tw-text-[10px] tw-text-white tw-font-bold">
                  {myPendingCount > 9 ? "9+" : myPendingCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {openTab !== 3 && hasEpks && <EpkStrip />}

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-24">
          {openTab === 3 ? (
            <div className="tw-pt-4">
              <MyApplicationsPanel />
            </div>
          ) : !hasEpks ? (
            <NoEpksState />
          ) : (
            <>
              {epkLoadFailed && <div className="tw-pt-4"><EpkLoadError /></div>}
              {openTab === 1 && (
                <div className="tw-pt-4">
                  <SubTabBar epk={selectedMobileEpk} />
                  <UserList epk={selectedMobileEpk} />
                </div>
              )}
              {openTab === 2 && (
                <>
                  <div className="tw-flex tw-rounded-full tw-bg-white/10 tw-p-1 tw-my-3">
                    {[["allRequests", t("All")], ["pending", t("Pending")], ["approved", t("Approved")], ["refused", t("Denied")]].map(([key, label]) => (
                      <button key={key} onClick={() => setFilter(key)} className={filterTabClass(filter === key)}>{label}</button>
                    ))}
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {(() => {
                      const filteredRequests = (selectedMobileEpk?.requests || []).filter(
                        req => filter === "allRequests" || req.status === filter
                      );
                      return filteredRequests.length > 0 ? filteredRequests.map((req) => (
                        <RequestCardMobile
                          key={req.user + req.comment}
                          request={req}
                          onMessageIconClick={handleMessageClick}
                          onDeny={() => handleDeny(req, selectedMobileEpk._id)}
                          onApprove={() => handleApprove(req, selectedMobileEpk._id)}
                        />
                      )) : (
                        <p className="tw-text-center tw-text-sm tw-text-white/30 tw-py-8 tw-m-0">{t("No requests for this EPK.")}</p>
                      );
                    })()}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <Sidebar role={user.role} />
      </div>

      {/* Desktop */}
      <div className="tw-hidden md:tw-block">
        <DashboardShell role={user.role} pageTitle={t("Notifications")}>
          {loading ? (
            <LoadingSpin />
          ) : (
            <div className={`tw-grid tw-h-full tw-gap-4 ${showLeftPanel ? "tw-grid-cols-1 md:tw-grid-cols-3" : "tw-grid-cols-1"}`}>

              {/* Left — EPK list (only for tabs 1 and 2 when user has EPKs) */}
              {showLeftPanel && (
                <div className="tw-overflow-auto tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-3">
                  <NotificationTextInfo />
                  {epkList.map((epk, index) => (
                    <div key={epk._id || index} onClick={() => handleSelectDesktopEpk(index)} className="tw-cursor-pointer">
                      <NotificationEpkCard epkInfo={epk} imgIsSelected={selectedEpk === index} />
                    </div>
                  ))}
                </div>
              )}

              {/* Right — detail panel */}
              <div className={`${showLeftPanel ? "tw-col-span-2" : ""} tw-flex tw-flex-col tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-overflow-hidden`}>

                {/* Main tabs */}
                <div className="tw-flex tw-border-b tw-border-white/10 tw-flex-shrink-0">
                  {[
                    { label: t("Activity"),  idx: 1 },
                    { label: t("Requests"),  idx: 2, badge: pendingCount,   badgeColor: "tw-bg-red-500"   },
                    { label: t("Applied"),   idx: 3, badge: myPendingCount, badgeColor: "tw-bg-amber-500" },
                  ].map(({ label, idx, badge, badgeColor }) => (
                    <button
                      key={label}
                      onClick={() => setOpenTab(idx)}
                      className={`tw-flex-1 tw-py-3 tw-text-sm tw-font-medium tw-transition-colors tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-2
                        ${idx > 1 ? "tw-border-l tw-border-white/10" : ""}
                        ${openTab === idx ? "tw-bg-[#712CB0] tw-text-white" : "tw-bg-transparent tw-text-white/50 hover:tw-bg-white/5 hover:tw-text-white"}`}
                    >
                      {label}
                      {badge > 0 && (
                        <span className={`tw-rounded-full ${badgeColor} tw-px-1.5 tw-text-[10px] tw-text-white tw-font-bold`}>
                          {badge > 9 ? "9+" : badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="tw-flex-1 tw-overflow-auto tw-p-4">
                  {epkLoadFailed && hasEpks && openTab !== 3 && <div className="tw-mb-4"><EpkLoadError /></div>}

                  {openTab === 1 && (
                    hasEpks ? (
                      <>
                        <SubTabBar epk={selectedDesktopEpk} />
                        <UserList epk={selectedDesktopEpk} />
                      </>
                    ) : (
                      <NoEpksState large />
                    )
                  )}

                  {openTab === 2 && (
                    hasEpks ? (
                      <div className="tw-flex tw-flex-col tw-gap-4">
                        <div className="tw-flex tw-rounded-xl tw-bg-white/10 tw-p-1 tw-w-fit tw-gap-1">
                          {[["allRequests", t("All")], ["pending", t("Pending")], ["approved", t("Approved")], ["refused", t("Refused")]].map(([key, label]) => (
                            <button
                              key={key}
                              onClick={() => setFilter(key)}
                              className={`tw-rounded-lg tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors tw-border-0 tw-cursor-pointer ${
                                filter === key ? "tw-bg-[#712CB0] tw-text-white" : "tw-bg-transparent tw-text-white/50 hover:tw-text-white"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {(() => {
                          const list = (selectedDesktopEpk?.requests || []).filter(
                            req => filter === "allRequests" || req.status === filter
                          );
                          return list.length > 0 ? list.map((request) => (
                            <div key={request.user + request.comment} className="tw-flex tw-flex-row tw-justify-between tw-border-b tw-border-white/10 tw-pb-3">
                              <div className="tw-w-2/3">
                                <RequestCard Request={request} />
                              </div>
                              <div className="tw-w-1/3 tw-flex tw-flex-col tw-items-end tw-justify-center tw-gap-2 tw-pr-2">
                                {request.comment && <p className="tw-text-xs tw-text-white/50 tw-text-right tw-m-0">{request.comment}</p>}
                                <RequestActions request={request} fepkId={selectedDesktopEpk._id} />
                              </div>
                            </div>
                          )) : (
                            <p className="tw-text-center tw-text-sm tw-text-white/30 tw-py-8 tw-m-0">{t("No requests matching this filter.")}</p>
                          );
                        })()}
                      </div>
                    ) : (
                      <NoEpksState large />
                    )
                  )}

                  {openTab === 3 && <MyApplicationsPanel />}

                </div>
              </div>
            </div>
          )}
        </DashboardShell>
      </div>
    </>
  );
}
